import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { TrendingUp, TrendingDown, Wallet, CheckCircle2, Clock, BarChart3, Target, Receipt, ChevronLeft, ChevronRight } from "lucide-react";

const fmt = (n) => `${(n || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export default function Cashflow() {
  const { user } = useAuth();
  const brand = user?.brand_color || "#EA580C";
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`;
  });

  useEffect(() => {
    api.get(`/dashboard/cashflow?month=${month}`).then((r) => setData(r.data));
  }, [month]);

  const shiftMonth = (delta) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  if (!data) return <div className="text-slate-500">Cargando…</div>;

  const trend = data.trend || [];
  const trendMax = Math.max(1, ...trend.map((t) => t.total));
  const weeklyMax = Math.max(1, ...data.weekly.map((w) => w.total));
  const monthLabel = new Date(data.month + "-01").toLocaleDateString("es-ES", { month: "long", year: "numeric" });

  return (
    <div data-testid="cashflow-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="kbar">Mis ingresos previstos</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tight mt-2">Cashflow</h1>
          <p className="text-slate-500 mt-2">Cuánto vas a cobrar este mes según los presupuestos aceptados.</p>
        </div>
        <div className="flex items-center gap-1 border border-slate-200 bg-white">
          <button onClick={() => shiftMonth(-1)} data-testid="prev-month" className="h-10 w-10 hover:bg-slate-50 inline-flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
          <span className="px-4 font-display font-bold capitalize" data-testid="current-month">{monthLabel}</span>
          <button onClick={() => shiftMonth(1)} data-testid="next-month" className="h-10 w-10 hover:bg-slate-50 inline-flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-slate-200 border border-slate-200 mt-8">
        <Card icon={Wallet} label="Previsto este mes" value={fmt(data.expected)} sub={
          <span className={`inline-flex items-center gap-1 text-xs font-bold ${data.diff_vs_prev >= 0 ? "text-green-600" : "text-red-600"}`}>
            {data.diff_vs_prev >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {data.diff_vs_prev >= 0 ? "+" : ""}{fmt(data.diff_vs_prev)} vs mes anterior
          </span>
        } accent brand={brand} testid="card-expected" />
        <Card icon={CheckCircle2} label="Cobrado" value={fmt(data.paid)} sub={data.expected ? `${Math.round((data.paid / data.expected) * 100)}% del previsto` : ""} testid="card-paid" />
        <Card icon={Clock} label="Pendiente" value={fmt(data.pending)} sub="por cobrar" testid="card-pending" />
        <Card icon={Target} label="Aceptación" value={`${data.accept_ratio}%`} sub={`${data.counts.accepted} aceptados`} testid="card-accept-ratio" />
        <Card icon={Receipt} label="Ticket medio" value={fmt(data.avg_ticket)} sub="por trabajo aceptado" testid="card-avg-ticket" />
      </div>

      {/* Two charts side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-slate-200 border border-slate-200 mt-px">
        {/* Weekly cashflow */}
        <div className="bg-white p-6" data-testid="weekly-chart">
          <div className="flex items-center justify-between mb-1">
            <div className="kbar">Cashflow semanal · {monthLabel}</div>
            <BarChart3 className="w-5 h-5 text-slate-400" />
          </div>
          <p className="text-xs text-slate-500 mb-6">Cuánto entrará cada semana del mes</p>
          <div className="grid grid-cols-5 gap-3 h-56 items-end">
            {data.weekly.map((w) => {
              const h = Math.max(2, (w.total / weeklyMax) * 100);
              return (
                <div key={w.week} className="flex flex-col items-center justify-end gap-2 h-full" data-testid={`week-${w.week}`}>
                  <span className="text-[10px] tabular-nums text-slate-500 font-bold">{w.total > 0 ? Math.round(w.total) + " €" : "—"}</span>
                  <div className="w-full transition-all duration-700" style={{ height: `${h}%`, background: brand, opacity: w.total > 0 ? 1 : 0.15 }} />
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Sem {w.week}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* 6-month trend */}
        <div className="bg-white p-6" data-testid="trend-chart">
          <div className="flex items-center justify-between mb-1">
            <div className="kbar">Tendencia · últimos 6 meses</div>
            <TrendingUp className="w-5 h-5 text-slate-400" />
          </div>
          <p className="text-xs text-slate-500 mb-6">Ingresos previstos por mes</p>
          <div className="grid grid-cols-6 gap-2 h-56 items-end">
            {trend.map((t) => {
              const h = Math.max(2, (t.total / trendMax) * 100);
              const isCurrent = t.month === data.month;
              const [y, m] = t.month.split("-");
              const lbl = new Date(parseInt(y), parseInt(m) - 1, 1).toLocaleDateString("es-ES", { month: "short" });
              return (
                <div key={t.month} className="flex flex-col items-center justify-end gap-2 h-full" data-testid={`trend-${t.month}`}>
                  <span className="text-[10px] tabular-nums text-slate-500 font-bold">{t.total > 0 ? Math.round(t.total) + " €" : ""}</span>
                  <div className="w-full transition-all duration-700" style={{ height: `${h}%`, background: isCurrent ? brand : "#CBD5E1" }} />
                  <span className={`text-[10px] uppercase tracking-wider font-bold ${isCurrent ? "text-slate-900" : "text-slate-400"}`}>{lbl}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Empty state */}
      {data.expected === 0 && (
        <div className="mt-px border border-slate-200 bg-slate-50 p-8 text-center">
          <p className="text-slate-600">Aún no tienes presupuestos aceptados para {monthLabel}. <Link to="/budgets" className="text-orange-600 font-bold">Ver presupuestos →</Link></p>
        </div>
      )}
    </div>
  );
}

function Card({ icon: Icon, label, value, sub, accent, brand, testid }) {
  return (
    <div className={`p-6 ${accent ? "text-white" : "bg-white"}`} style={accent ? { background: brand || "#EA580C" } : {}} data-testid={testid}>
      <Icon className={`w-5 h-5 ${accent ? "text-white/80" : "text-slate-400"}`} />
      <div className="mt-3 text-2xl font-display font-extrabold tabular-nums">{value}</div>
      <div className={`text-xs ${accent ? "text-white/90" : "text-slate-500"} mt-1`}>{label}</div>
      <div className={`mt-2 text-xs ${accent ? "text-white/90" : "text-slate-400"}`}>{sub}</div>
    </div>
  );
}
